// DISPLAY-ONLY sentence split (mirror of workers/src/lib/sentenceSplit.ts). A multi-sentence bullet (e.g. an
// Associate-Physician entry that lists ~10 duties as one paragraph) is shown as SEPARATE LINES instead of a wall of
// prose. It inserts NO words and changes NO words — it only splits the user's OWN verbatim text on sentence boundaries
// for rendering. The stored bullet_text is untouched (this is never on a write path). Handles abbreviations + decimals.

const PROTECT = String.fromCharCode(1); // sentinel replacing a non-boundary period during the split, restored to '.' after
// Abbreviations whose trailing period is NOT a sentence end (the period belongs to the abbreviation).
const ABBR = /\b(e\.g|i\.e|etc|vs|cf|approx|Dr|Drs|Mr|Mrs|Ms|Prof|St|Ave|No|Fig|Inc|Ltd|Co|Corp|Dept|al|Ph\.D|M\.D|B\.Sc|M\.Sc|U\.S|U\.K|a\.m|p\.m)\./gi;
// A real sentence boundary: a .!? then whitespace then the start of the next sentence (capital / digit / quote / paren).
const BOUNDARY = /(?<=[.!?])\s+(?=[A-Z0-9"'(\[])/;

/** Split a bullet's prose into its sentences (≥1). A single-sentence bullet returns `[text]` unchanged. Pure, display-only.
 *  A NEWLINE is also a hard boundary: some CVs separate bullet-sentences with line breaks and NO period — those must
 *  split into points just like period-delimited ones (otherwise they render as one lumped, un-markable blob). */
export function splitIntoLines(text: string): string[] {
  const s = (text ?? '').trim();
  if (!s) return [];
  // First split on newlines (a hard boundary regardless of punctuation), then sentence-split each line on '. '.
  const out: string[] = [];
  for (const raw of s.split(/\n+/)) {
    const line = raw.trim();
    if (!line) continue;
    const protectedText = line
      .replace(/(\d)\.(\d)/g, `$1${PROTECT}$2`)        // decimals: 5.5, 1.0
      .replace(ABBR, (m) => m.replace(/\./g, PROTECT)); // abbreviations: Dr., e.g., i.e.
    for (const p of protectedText.split(BOUNDARY)) {
      const sent = p.split(PROTECT).join('.').trim();
      if (sent) out.push(sent);
    }
  }
  return out.length ? out : [s];
}
